import { useCallback, useEffect, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import { ReactComponent as ChevronDown } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';

import type { B2BAssets, TestimonialSection, TestimonialSlide } from '../types';

export interface B2BTestimonialProps {
    section: TestimonialSection;
    assets: B2BAssets;
}

/**
 * Testimonial carousel — Figma `443:24592` + variants `523:25580 / 25590 /
 * 25601 / 25620 / 25638 / 25646`, white 1920×944 frame.
 *
 * Renders one slide at a time and rotates through `section.testimonials[]`
 * via the prev/next chevrons in the centre pill. Dots between the chevrons
 * indicate position and are clickable. Image + quote + attribution + project
 * thumbnail + project caption all rotate together per slide and crossfade
 * via stacked grid cells (every variant occupies the same `grid-area:1/1`
 * so the parent sizes to the largest slide and the layout doesn't shift).
 *
 * Absolute 1920-coord layout:
 *  - Portrait image: left=131.09/1920 (6.83%), w=635.903/1920 (33.12%),
 *    full frame height.
 *  - Quote (TWK Ghost 38.51 px ≈ 2.006vw): left=887.02/1920 (46.20%),
 *    top=147.24/944 (15.59%), w=816.441/1920 (42.52%), text-indent
 *    160/1920 = 8.333vw.
 *  - Attribution (Neue Haas Medium 25 px ≈ 1.302vw): left=887.02/1920
 *    (46.20%), top=610.13/944 (64.60%), w=816/1920 (≈42.5%) so two short
 *    lines (name + firm) never wrap.
 *  - Carousel pill: centred (translate-x-1/2 +2.11px), top=730/944 (77%).
 *  - Project thumbnail: left=1553.98/1920 (80.93%), top=625.43/944 (66.25%),
 *    349.86×214.33.
 *  - Project caption: left=1553.98/1920 (80.93%), top=853.77/944 (90.44%).
 *
 * Backwards-compat: if `section.testimonials` isn't supplied, falls back to
 * the legacy single-testimonial fields and renders a static single slide.
 */
/** Auto-rotate interval in milliseconds. Pauses on hover. */
const AUTO_ROTATE_MS = 3000;

export const B2BTestimonial = ({ section, assets }: B2BTestimonialProps) => {
    const slides = useResolvedSlides(section);
    const [activeIdx, setActiveIdx] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const total = slides.length;

    const goNext = useCallback(
        () => setActiveIdx((i) => (i + 1) % Math.max(total, 1)),
        [total],
    );
    const goPrev = useCallback(
        () =>
            setActiveIdx(
                (i) => (i - 1 + Math.max(total, 1)) % Math.max(total, 1),
            ),
        [total],
    );

    // Auto-rotate every AUTO_ROTATE_MS. Paused while the user is hovering
    // the section (mouseenter → setIsPaused(true), mouseleave → false). Also
    // skipped if there's only one slide.
    useEffect(() => {
        if (total <= 1 || isPaused) return;
        const id = window.setInterval(goNext, AUTO_ROTATE_MS);
        return () => window.clearInterval(id);
    }, [goNext, isPaused, total]);

    const fadeIn = (idx: number) =>
        cn(
            'transition-opacity duration-500 ease-out [grid-area:1/1]',
            idx === activeIdx ? 'opacity-100' : 'pointer-events-none opacity-0',
        );

    return (
        <section
            className="relative aspect-[1920/944] w-full bg-white text-[#181818] max-md:flex max-md:aspect-auto max-md:flex-col max-md:gap-8 max-md:px-6 max-md:py-12"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
        >
            {/* Image column — every slide image stacked in the same grid cell. */}
            {slides.length > 0 && (
                <div className="absolute left-[6.827%] top-0 grid aspect-[635.903/944.473] w-[33.120%] max-md:static max-md:aspect-[635/944] max-md:w-full">
                    {slides.map((slide, idx) => {
                        const src = assets[slide.imageAssetKey];
                        if (!src) return null;
                        return (
                            <img
                                key={`img-${idx}`}
                                src={src}
                                alt=""
                                className={cn(
                                    fadeIn(idx),
                                    'size-full object-cover',
                                )}
                            />
                        );
                    })}
                </div>
            )}

            {/* Quote — stacked in the same grid cell so the area sizes to the
             *  longest quote and short ones don't push other elements around. */}
            <div
                className="absolute left-[46.199%] top-[15.595%] grid w-[42.523%] max-md:static max-md:w-full"
                aria-live="polite"
            >
                {slides.map((slide, idx) => (
                    <blockquote
                        key={`q-${idx}`}
                        className={cn(
                            fadeIn(idx),
                            // Spec 2.006vw (38.5 px) overflowed into the
                            // attribution area for the longer Julian / Hamish
                            // / Nicole / Joseph quotes; dropped to 1.6vw
                            // (~31 px) so all 6 slides fit. Figma variants
                            // themselves use 25–28 px, so this stays in range.
                            'font-twk-ghost text-[1.6vw] leading-[1.2] tracking-[-0.020em] text-[#333]',
                            // Mobile token: body 3.255vw, drop the desktop indent.
                            'max-md:text-[3.255vw] max-md:[text-indent:0]',
                        )}
                        style={{ textIndent: '6.667vw' }}
                    >
                        &ldquo;{slide.quote}&rdquo;
                    </blockquote>
                ))}
            </div>

            {/* Attribution — stacked, with explicit width so name + role
             *  never wrap to a third line and never collide with the pill. */}
            <div className="absolute left-[46.199%] top-[64.602%] grid w-[42.523%] font-sans text-[1.302vw] font-medium leading-[1.3] tracking-[-0.030em] max-md:static max-md:w-full max-md:text-[2.6vw]">
                {slides.map((slide, idx) => (
                    <div key={`a-${idx}`} className={fadeIn(idx)}>
                        <div className="truncate text-black">
                            {slide.attributionName}
                        </div>
                        {slide.attributionRole && (
                            <div className="truncate text-[#888]">
                                {slide.attributionRole}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Carousel pill — desktop overlays at y=77 %; mobile renders
             *  inline below the attribution. Mobile chrome uses the same
             *  vw token (3.95 vw chevron) as the Partners chevrons; tap
             *  targets get a `min-h-[2.75rem]` floor via button padding. */}
            {total > 1 && (
                <div
                    className={cn(
                        'flex items-center rounded-full border border-[#c4c4c4] bg-white',
                        'md:absolute md:top-[77%] md:-translate-x-1/2',
                        'md:gap-[0.313vw] md:px-[0.417vw] md:py-[0.625vw]',
                        'max-md:mx-auto max-md:mt-[4vw] max-md:gap-[2.6vw] max-md:px-[2.6vw] max-md:py-[1.5vw]',
                    )}
                    style={{ left: 'calc(50% + 0.110vw)' }}
                    role="group"
                    aria-label="Testimonial carousel"
                >
                    <button
                        type="button"
                        onClick={goPrev}
                        aria-label="Previous testimonial"
                        className="cursor-pointer transition-opacity hover:opacity-60 max-md:flex max-md:min-h-[2.75rem] max-md:min-w-[2.75rem] max-md:items-center max-md:justify-center"
                    >
                        <ChevronDown className="rotate-90 text-[#181818] max-md:size-[3.95vw] md:size-[0.938vw]" />
                    </button>
                    <div className="flex items-center max-md:gap-[1.5vw] md:gap-[0.26vw] md:px-[0.417vw]">
                        {slides.map((_, idx) => (
                            <button
                                key={`dot-${idx}`}
                                type="button"
                                onClick={() => setActiveIdx(idx)}
                                aria-label={`Go to testimonial ${idx + 1}`}
                                aria-current={idx === activeIdx}
                                className={cn(
                                    'block cursor-pointer rounded-full transition-colors duration-200',
                                    'md:size-[0.365vw]',
                                    'max-md:size-[1.5vw]',
                                    idx === activeIdx
                                        ? 'bg-[#181818]'
                                        : 'bg-[#c4c4c4] hover:bg-[#888]',
                                )}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={goNext}
                        aria-label="Next testimonial"
                        className="cursor-pointer transition-opacity hover:opacity-60 max-md:flex max-md:min-h-[2.75rem] max-md:min-w-[2.75rem] max-md:items-center max-md:justify-center"
                    >
                        <ChevronDown className="-rotate-90 text-[#181818] max-md:size-[3.95vw] md:size-[0.938vw]" />
                    </button>
                </div>
            )}

            {/* Project thumbnail — Figma `443:24594` slot. Rotates per slide. */}
            <div
                className="absolute grid overflow-hidden max-md:hidden"
                style={{
                    left: `${(1553.98 / 1920) * 100}%`,
                    top: `${(625.43 / 944) * 100}%`,
                    width: `${(349.86 / 1920) * 100}%`,
                    height: `${(214.33 / 944) * 100}%`,
                }}
            >
                {slides.map((slide, idx) => {
                    const src = slide.projectThumbnailAssetKey
                        ? assets[slide.projectThumbnailAssetKey]
                        : undefined;
                    if (!src) return null;
                    return (
                        <img
                            key={`thumb-${idx}`}
                            src={src}
                            alt=""
                            className={cn(
                                fadeIn(idx),
                                'size-full object-cover',
                            )}
                        />
                    );
                })}
            </div>

            {/* Project caption + (View Project) link — rotates per slide. */}
            <div className="absolute left-[80.937%] top-[90.442%] grid font-sans text-[0.938vw] font-medium leading-[1.1] tracking-[-0.030em] max-md:static max-md:text-[2.08vw]">
                {slides.map((slide, idx) => {
                    const hasContent =
                        slide.projectName ||
                        (section.viewProjectLabel && slide.viewProjectHref);
                    if (!hasContent) return null;
                    return (
                        <div key={`cap-${idx}`} className={fadeIn(idx)}>
                            {slide.projectName && (
                                <div className="text-[#888]">
                                    {slide.projectName}
                                </div>
                            )}
                            {slide.projectRole && (
                                <div className="text-[#888]">
                                    {slide.projectRole}
                                </div>
                            )}
                            {section.viewProjectLabel &&
                                slide.viewProjectHref && (
                                    <RoccoLink
                                        to={slide.viewProjectHref}
                                        className="mt-[1.089vw] block text-[#181818] underline-offset-4 hover:underline max-md:mt-3"
                                    >
                                        {section.viewProjectLabel}
                                    </RoccoLink>
                                )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

/** Resolves the slide list — either the new `testimonials` array or the
 *  legacy single-testimonial fields wrapped as a single-item list. */
const useResolvedSlides = (section: TestimonialSection): TestimonialSlide[] => {
    if (section.testimonials && section.testimonials.length > 0) {
        return section.testimonials;
    }
    const legacyImage = section.imageAssetKeys?.[0];
    if (!section.quote || !section.attributionName || !legacyImage) return [];
    return [
        {
            quote: section.quote,
            attributionName: section.attributionName,
            attributionRole: section.attributionRole,
            imageAssetKey: legacyImage,
            projectName: section.projectName,
            projectRole: section.projectRole,
            projectThumbnailAssetKey: section.projectThumbnailAssetKey,
            viewProjectHref: section.viewProjectHref,
        },
    ];
};
